import React from "react"
import { Box, Link as ChakraLink } from "@chakra-ui/react"
import { FaArrowUpRightFromSquare } from "react-icons/fa6"
import { useNavigate } from "react-router-dom"
import { useNavigation } from "../providers/NavigationProvider"
import { availableSections, useSection } from "../providers/helpers/useRoute"
import { useRoute } from "../providers/helpers/useRoute"
import { Tooltip } from "../chakra/Tooltip"
import { Fragment } from "react/jsx-runtime"

export interface LinkProps {
  href?: string
  variant?: "primary" | "secondary"
  children: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  className?: string
  fontSize?: string
  internal?: boolean
  external?: boolean
  iconSize?: number
  truncate?: boolean
  maxWidth?: string
  tooltip?: boolean
  deLink?: boolean
  [key: string]: any // Allow other props to be passed through
}

/**
 * Standardized Link component with dual-action behavior:
 * - Click underlined text: opens in current tab
 * - Click external icon: opens in new tab (for external URLs or when external=true)
 *
 * Automatically detects external URLs (http/https) and shows external icon.
 * Internal/relative URLs show underlined text, or dual-action when external=true.
 */
export const Link: React.FC<LinkProps> = ({
  variant = "primary",
  children,
  onClick,
  internal = false,
  external = false,
  truncate = false,
  maxWidth,
  tooltip = false,
  deLink = false,
  iconSize,
  ...props
}) => {
  let href = props.href
  delete props.href

  // Detect if this is an external URL
  const isExternal = Boolean(href?.startsWith("http://") || href?.startsWith("https://") || external)
  const navigate = useNavigate()
  const hubNavigate = useNavigation() // Hub-level navigation when available
  const navigableSections = availableSections()
  const section = useSection()
  const route = useRoute()

  const isCrossSection = navigableSections.some((section) => href?.startsWith(`/${section}`))
  const deprecatedDashboard = section && route.includes(section)

  if (import.meta.env.VITE_ENV !== "dev" && section && href && !href.startsWith(`/${section}`) && !isCrossSection) {
    href = `/${section}${href}`
  }

  // Handle text click - always opens in current tab
  const handleTextClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (onClick) {
      onClick(e)
    }

    if (!href) return

    if (hubNavigate && isCrossSection) {
      hubNavigate(href)
    } else {
      const targetUrl = deprecatedDashboard ? href : href.replace(`/${section}`, "")
      navigate(targetUrl)
    }
  }

  // Handle icon click - always opens in new tab
  const handleNewTabClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation() // Prevent text click handler from firing

    let targetUrl = href

    window.open(targetUrl, "_blank", "noopener,noreferrer")
  }

  const internalClick = deprecatedDashboard && isCrossSection ? handleNewTabClick : handleTextClick

  if (deLink) return <>{children}</>

  const linkElement = (
    <ChakraLink
      href={href}
      css={{
        color: "var(--chakra-colors-primary)",
        fontWeight: variant === "primary" ? "bold" : "semibold",
        textDecoration: "none",
        ...(truncate
          ? {
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              maxWidth: maxWidth || "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "1.25",
              maxHeight: "2.5em",
              wordBreak: "break-word",
              verticalAlign: "middle",
            }
          : {
              ...(maxWidth ? { maxWidth } : {}),
            }),
        "&:hover": {
          color: "#39AED2",
          textDecoration: "underline",
        },
      }}
      onClick={internalClick}
      {...props}
    >
      {children}
    </ChakraLink>
  )

  if (isExternal) {
    // External link with dual-action behavior
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          minWidth: 0,
          maxWidth: maxWidth || "100%",
          overflow: truncate ? "hidden" : undefined,
        }}
      >
        {tooltip ? <Tooltip content={children}>{linkElement}</Tooltip> : linkElement}
        <ChakraLink
          as="button"
          css={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            minWidth: "auto",
            height: "auto",
            padding: 0,
            flexShrink: 0,
            backgroundColor: "transparent",
            color: "var(--chakra-colors-primary)",
            "&:hover": {
              color: "#39AED2",
              backgroundColor: "transparent",
            },
          }}
          onClick={handleNewTabClick}
          aria-label="Open in new tab"
        >
          <FaArrowUpRightFromSquare size={iconSize || 12} />
        </ChakraLink>
      </span>
    )
  }

  // Internal link - simple underlined text
  return tooltip ? <Tooltip content={children}>{linkElement}</Tooltip> : linkElement
}

export default Link
